import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar, Clock, Loader2, Plus, Play, Pause, Trash2, X,
  ChevronRight, CheckCircle2, AlertTriangle, ExternalLink, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Human-readable weekday for schedule display / picker.
const WEEKDAYS = [
  { key: 0, short: "Mon", long: "Monday" },
  { key: 1, short: "Tue", long: "Tuesday" },
  { key: 2, short: "Wed", long: "Wednesday" },
  { key: 3, short: "Thu", long: "Thursday" },
  { key: 4, short: "Fri", long: "Friday" },
  { key: 5, short: "Sat", long: "Saturday" },
  { key: 6, short: "Sun", long: "Sunday" },
];

const RECURRENCE_OPTIONS = [
  { key: "daily",    label: "Daily",         desc: "Every day at chosen time" },
  { key: "weekly",   label: "Weekly on…",    desc: "Every week on the picked weekday" },
  { key: "biweekly", label: "Every 2 weeks", desc: "Every 14 days on the picked weekday" },
  { key: "monthly",  label: "Monthly on…",   desc: "Every month on the picked day (1–28)" },
  { key: "custom",   label: "Custom…",       desc: "Every N days" },
];

// Format an ISO UTC timestamp as an IST short string, e.g. "Mon 4 Sep, 09:30"
const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      weekday: "short", day: "numeric", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

// Human summary for a schedule's recurrence — used in list rows.
const describeRecurrence = (s) => {
  const t = `${String(s.hour).padStart(2, "0")}:${String(s.minute).padStart(2, "0")}`;
  if (s.recurrence === "daily") return `Daily · ${t} IST`;
  if (s.recurrence === "weekly") {
    const d = WEEKDAYS.find((w) => w.key === s.day_of_week)?.short || "?";
    return `Weekly · ${d} · ${t} IST`;
  }
  if (s.recurrence === "biweekly") {
    const d = WEEKDAYS.find((w) => w.key === s.day_of_week)?.short || "?";
    return `Every 2 weeks · ${d} · ${t} IST`;
  }
  if (s.recurrence === "monthly") return `Monthly · day ${s.day_of_month} · ${t} IST`;
  if (s.recurrence === "custom") return `Every ${s.custom_days} days · ${t} IST`;
  return "—";
};

// ─── Create-Schedule Modal ──────────────────────────────────────────
const CreateScheduleModal = ({ categories, onClose, onCreated }) => {
  const [categoryId, setCategoryId] = useState("");
  const [market, setMarket] = useState("in");
  const [recurrence, setRecurrence] = useState("weekly");
  const [dayOfWeek, setDayOfWeek] = useState(0);   // Monday
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [customDays, setCustomDays] = useState(7);
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);
  const [maxProducts, setMaxProducts] = useState(50);
  const [saving, setSaving] = useState(false);

  const category = useMemo(
    () => categories.find((c) => c.category_id === categoryId),
    [categoryId, categories],
  );
  // Auto-fallback market if selected side has no URL
  useEffect(() => {
    if (!category) return;
    if (market === "in" && !category.url_in && category.url_com) setMarket("com");
    if (market === "com" && !category.url_com && category.url_in) setMarket("in");
  }, [category, market]);

  const canSubmit = !!categoryId && !!category
    && ((market === "in" && category.url_in) || (market === "com" && category.url_com))
    && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {
        category_id: categoryId,
        market,
        recurrence,
        hour, minute,
        max_products_per_url: Number(maxProducts) || 50,
        engine: "apify",
      };
      if (recurrence === "weekly" || recurrence === "biweekly") payload.day_of_week = Number(dayOfWeek);
      if (recurrence === "monthly") payload.day_of_month = Math.max(1, Math.min(28, Number(dayOfMonth) || 1));
      if (recurrence === "custom") payload.custom_days = Math.max(1, Number(customDays) || 1);
      const r = await axios.post(`${API}/bestseller-schedules`, payload);
      toast.success(`Schedule created — next run ${fmtIST(r.data.next_run)} IST`);
      onCreated(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create schedule");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog" aria-modal="true"
      data-testid="bs-schedule-modal"
    >
      <div className="w-[560px] max-w-[95vw] bg-white rounded-2xl shadow-2xl border border-gray-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-purple-500">Amazon Market Research</div>
            <h3 className="text-base font-bold text-gray-900">New scheduled run</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="bs-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Category */}
          <div className="space-y-1.5">
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500">Best-Seller category</label>
            {categories.length === 0 ? (
              <div className="text-xs text-rose-600 border border-rose-200 bg-rose-50 rounded-lg px-3 py-2">
                No Best-Seller categories saved yet. Add one from <b>Control Panel → Best Sellers</b>.
              </div>
            ) : (
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-purple-400 focus:ring-1 focus:ring-purple-200 outline-none"
                data-testid="bs-modal-category"
              >
                <option value="">Choose a category…</option>
                {categories.map((c) => (
                  <option key={c.category_id} value={c.category_id}>{c.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Market */}
          {category && (
            <div className="space-y-1.5">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500">Marketplace</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: "in",  label: "Amazon.in",  url: category.url_in },
                  { key: "com", label: "Amazon.com", url: category.url_com },
                ].map((opt) => {
                  const disabled = !opt.url;
                  const active = market === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      disabled={disabled}
                      onClick={() => !disabled && setMarket(opt.key)}
                      className={`text-left px-3 py-2 rounded-lg border text-xs transition-all ${
                        disabled ? "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed"
                        : active ? "border-purple-400 bg-purple-50 text-purple-800"
                                 : "border-gray-200 hover:border-purple-300 text-gray-700"
                      }`}
                      data-testid={`bs-modal-market-${opt.key}`}
                    >
                      <div className="font-semibold flex items-center gap-1.5">
                        {opt.label}
                        {disabled && <span className="text-[9px] uppercase tracking-wider text-gray-400">No URL</span>}
                      </div>
                      {opt.url && (
                        <div className="mt-0.5 text-[10px] text-gray-500 truncate" title={opt.url}>{opt.url}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recurrence */}
          <div className="space-y-1.5">
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500">Recurrence</label>
            <select
              value={recurrence}
              onChange={(e) => setRecurrence(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-purple-400 focus:ring-1 focus:ring-purple-200 outline-none"
              data-testid="bs-modal-recurrence"
            >
              {RECURRENCE_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
            <div className="text-[10.5px] text-gray-500">{RECURRENCE_OPTIONS.find((o) => o.key === recurrence)?.desc}</div>
          </div>

          {/* Recurrence details */}
          {(recurrence === "weekly" || recurrence === "biweekly") && (
            <div className="space-y-1.5">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500">Weekday</label>
              <div className="grid grid-cols-7 gap-1">
                {WEEKDAYS.map((w) => (
                  <button
                    key={w.key}
                    type="button"
                    onClick={() => setDayOfWeek(w.key)}
                    className={`px-2 py-1.5 text-[11px] font-semibold rounded-md border ${
                      dayOfWeek === w.key
                        ? "border-purple-400 bg-purple-500 text-white"
                        : "border-gray-200 text-gray-600 hover:border-purple-300"
                    }`}
                    data-testid={`bs-modal-dow-${w.key}`}
                  >
                    {w.short}
                  </button>
                ))}
              </div>
            </div>
          )}
          {recurrence === "monthly" && (
            <div className="space-y-1.5">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500">Day of month (1–28)</label>
              <input
                type="number" min={1} max={28}
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-purple-400 focus:ring-1 focus:ring-purple-200 outline-none"
                data-testid="bs-modal-dom"
              />
              <div className="text-[10.5px] text-gray-500">Capped at 28 so short months don't skip runs.</div>
            </div>
          )}
          {recurrence === "custom" && (
            <div className="space-y-1.5">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500">Every N days</label>
              <input
                type="number" min={1}
                value={customDays}
                onChange={(e) => setCustomDays(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-purple-400 focus:ring-1 focus:ring-purple-200 outline-none"
                data-testid="bs-modal-custom-days"
              />
            </div>
          )}

          {/* Time */}
          <div className="space-y-1.5">
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500">Time (IST)</label>
            <div className="flex items-center gap-2">
              <input
                type="number" min={0} max={23}
                value={hour}
                onChange={(e) => setHour(Math.max(0, Math.min(23, Number(e.target.value) || 0)))}
                className="w-20 px-3 py-2 text-sm border border-gray-200 rounded-lg text-center tabular-nums focus:border-purple-400 focus:ring-1 focus:ring-purple-200 outline-none"
                data-testid="bs-modal-hour"
              />
              <span className="font-bold text-gray-400">:</span>
              <input
                type="number" min={0} max={59}
                value={minute}
                onChange={(e) => setMinute(Math.max(0, Math.min(59, Number(e.target.value) || 0)))}
                className="w-20 px-3 py-2 text-sm border border-gray-200 rounded-lg text-center tabular-nums focus:border-purple-400 focus:ring-1 focus:ring-purple-200 outline-none"
                data-testid="bs-modal-minute"
              />
              <span className="text-[11px] text-gray-500">{String(hour).padStart(2, "0")}:{String(minute).padStart(2, "0")} IST</span>
            </div>
          </div>

          {/* Products cap */}
          <div className="space-y-1.5">
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500">Products to scrape per run (max 100)</label>
            <input
              type="number" min={1} max={100}
              value={maxProducts}
              onChange={(e) => setMaxProducts(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-purple-400 focus:ring-1 focus:ring-purple-200 outline-none"
              data-testid="bs-modal-max"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-gray-600 hover:text-gray-900"
            data-testid="bs-modal-cancel"
          >Cancel</button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold"
            data-testid="bs-modal-submit"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            Create schedule
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Panel ─────────────────────────────────────────────────────
const BestSellerSchedulesPanel = () => {
  const [categories, setCategories] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [cats, sch, rns] = await Promise.all([
          axios.get(`${API}/bestseller-categories`),
          axios.get(`${API}/bestseller-schedules`),
          axios.get(`${API}/bestseller-schedules/runs?limit=50`),
        ]);
        if (cancelled) return;
        setCategories(cats.data || []);
        setSchedules(sch.data || []);
        setRuns(rns.data || []);
      } catch {
        if (!cancelled) toast.error("Failed to load schedules");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [refreshTick]);

  // Auto-refresh every 30s so in-flight runs surface as they progress
  useEffect(() => {
    const t = setInterval(() => setRefreshTick((v) => v + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const toggleActive = async (sch) => {
    try {
      const r = await axios.put(`${API}/bestseller-schedules/${sch.schedule_id}`, { active: !sch.active });
      setSchedules((prev) => prev.map((s) => s.schedule_id === sch.schedule_id ? r.data : s));
      toast.success(sch.active ? "Schedule paused" : "Schedule resumed");
    } catch { toast.error("Failed to update schedule"); }
  };
  const removeSchedule = async (sch) => {
    if (!window.confirm(`Delete schedule for "${sch.category_name}"? Historical runs are kept.`)) return;
    try {
      await axios.delete(`${API}/bestseller-schedules/${sch.schedule_id}`);
      setSchedules((prev) => prev.filter((s) => s.schedule_id !== sch.schedule_id));
      toast.success("Schedule deleted");
    } catch { toast.error("Failed to delete schedule"); }
  };

  const statusPill = (status) => {
    const base = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold";
    if (status === "completed") return <span className={`${base} bg-emerald-100 text-emerald-700`}><CheckCircle2 className="w-3 h-3" /> Completed</span>;
    if (status === "failed")    return <span className={`${base} bg-rose-100 text-rose-700`}><AlertTriangle className="w-3 h-3" /> Failed</span>;
    if (status === "processing") return <span className={`${base} bg-indigo-100 text-indigo-700`}><Loader2 className="w-3 h-3 animate-spin" /> Running</span>;
    return <span className={`${base} bg-gray-100 text-gray-500`}>{status || "—"}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-gray-500" data-testid="bs-schedules-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading schedules…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="bs-schedules-panel">
      {/* Header */}
      <div className="flex items-center gap-3 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl px-5 py-4">
        <div className="w-10 h-10 rounded-xl bg-white border border-purple-300 flex items-center justify-center">
          <Calendar className="w-5 h-5 text-purple-500" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-gray-900">Scheduled Best-Seller runs</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">Automated recurring Market Research scrapes for saved Best-Seller categories. Add categories in <b>Control Panel → Best Sellers</b>.</p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold"
          data-testid="bs-new-schedule-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> New schedule
        </Button>
        <button
          onClick={() => setRefreshTick((v) => v + 1)}
          className="text-gray-400 hover:text-gray-700 p-2"
          title="Refresh"
          data-testid="bs-refresh-btn"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Schedules list */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="bs-schedules-list">
        <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Active schedules · {schedules.length}</div>
        </div>
        {schedules.length === 0 ? (
          <div className="px-5 py-10 text-center text-xs text-gray-400">
            No schedules yet. Click <b>New schedule</b> to create your first recurring run.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {schedules.map((s) => (
              <div key={s.schedule_id} className="px-4 py-3 flex items-center gap-4" data-testid={`bs-schedule-row-${s.schedule_id}`}>
                <div className={`w-2 h-2 rounded-full shrink-0 ${s.active ? "bg-emerald-500 animate-pulse" : "bg-gray-300"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900 truncate">{s.category_name}</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 border border-gray-200 rounded px-1.5">
                      Amazon.{s.market}
                    </span>
                    {s.last_status && (
                      <span className="text-[10px]">{statusPill(s.last_status)}</span>
                    )}
                  </div>
                  {s.last_status === "failed" && s.last_error && (
                    <div data-testid={`bs-schedule-last-error-${s.schedule_id}`} className="mt-1 text-[10.5px] text-rose-600 truncate" title={s.last_error}>
                      {s.last_error}
                    </div>
                  )}
                  <div className="mt-0.5 flex items-center gap-3 text-[11px] text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {describeRecurrence(s)}
                    </span>
                    <span className="text-gray-300">·</span>
                    <span>Next: <span className="font-semibold text-gray-700">{s.active ? fmtIST(s.next_run) : "paused"}</span></span>
                    {s.last_run && (
                      <>
                        <span className="text-gray-300">·</span>
                        <span>Last: <span className="font-semibold text-gray-700">{fmtIST(s.last_run)}</span></span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => toggleActive(s)}
                  className={`p-2 rounded-lg border ${s.active ? "border-amber-200 text-amber-600 hover:bg-amber-50" : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"}`}
                  title={s.active ? "Pause" : "Resume"}
                  data-testid={`bs-schedule-toggle-${s.schedule_id}`}
                >
                  {s.active ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                </button>
                <button
                  onClick={() => removeSchedule(s)}
                  className="p-2 rounded-lg border border-rose-200 text-rose-500 hover:bg-rose-50"
                  title="Delete schedule"
                  data-testid={`bs-schedule-delete-${s.schedule_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Run timeline */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="bs-runs-list">
        <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Run history · latest 50</div>
        </div>
        {runs.length === 0 ? (
          <div className="px-5 py-10 text-center text-xs text-gray-400">
            No runs yet. When your first scheduled scrape kicks off it will appear here.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {runs.map((r) => (
              <div key={r.run_id} className="px-4 py-3 flex items-center gap-4" data-testid={`bs-run-row-${r.run_id}`}>
                <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900 truncate">{r.category_name}</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 border border-gray-200 rounded px-1.5">
                      Amazon.{r.market}
                    </span>
                    {statusPill(r.status)}
                    {r.retry_of && (
                      <span className="text-[10px] font-semibold text-amber-700 border border-amber-200 bg-amber-50 rounded px-1.5">RETRY</span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-3 text-[11px] text-gray-500">
                    <span>Started: <span className="font-semibold text-gray-700">{fmtIST(r.started_at)}</span></span>
                    {r.completed_at && (
                      <>
                        <span className="text-gray-300">·</span>
                        <span>Finished: <span className="font-semibold text-gray-700">{fmtIST(r.completed_at)}</span></span>
                      </>
                    )}
                    {typeof r.result_count === "number" && (
                      <>
                        <span className="text-gray-300">·</span>
                        <span>{r.result_count} products</span>
                      </>
                    )}
                  </div>
                  {r.error && (
                    <div className="mt-1 text-[10.5px] text-rose-600 bg-rose-50 border border-rose-100 rounded px-2 py-1 truncate" title={r.error}>
                      {r.error}
                    </div>
                  )}
                </div>
                {r.url && (
                  <a
                    href={r.url}
                    target="_blank" rel="noreferrer"
                    className="text-gray-400 hover:text-purple-500 p-2 shrink-0"
                    title="Open source URL"
                    data-testid={`bs-run-url-${r.run_id}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <CreateScheduleModal
          categories={categories}
          onClose={() => setShowModal(false)}
          onCreated={(sch) => setSchedules((prev) => [sch, ...prev])}
        />
      )}
    </div>
  );
};

export default BestSellerSchedulesPanel;
